import {
  Button,
  Divider,
  Grid,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import * as React from "react";
import { fetchData } from "../../../../common/services/ApiClient";
import { ApiBaseUrl } from "../../../../config";
import { FormSelectChoice, KeyValue, Query } from "../../../../interfaces";
import { selectedStatesAlpha } from "../../../../refdata/FilterOptions";
import {
  marketShareLocationOptions,
  marketShareLocationOptions2,
  numericalRegEx,
  proFormaLocationOptions,
} from "../../../../refdata/constants";
import SelectFilterField from "../../../SelectFilterField";
import TextFilterField from "../../../TextFilterField";
import BranchOfficeMarketSelections from "../../BranchOfficeMarketSelections";
import { MarketShareForm } from "../MarketShareSearchForm";
const useStyles = makeStyles(theme => ({
  title: { paddingTop: 16, lineHeight: "1rem" },
  buttonsGrid: {
    marginBottom: "1rem",
  },
  addRemoveButtons: {
    "&.MuiButton-root": {
      width: "100%",
      padding: 0,
      fontWeight: 400,
      margin: 0,
    },
  },
  addButton: {
    "&.MuiButton-root": {
      "backgroundColor": "#ffffff",
      "&:hover, &.Mui-focusVisible": {
        color: "#ffffff",
        backgroundColor: theme.palette.primary.main,
      },
    },
  },
}));

const divider = (
  <Divider
    style={{ margin: 0, backgroundColor: "#acc8df" }}
    variant="middle"
    component="li"
  />
);

interface Props {
  appState: Query;
  searchForm: MarketShareForm;
  disabled: boolean;
  setDisabled: (disabled: boolean) => void;
  setSearchForm: (searchForm: MarketShareForm) => void;
}

export default function DepositMarketShareFormOptions({
  appState,
  searchForm,
  disabled,
  setDisabled,
  setSearchForm,
}: Props) {
  const classes = useStyles();

  const [msa, setMsa] = React.useState<FormSelectChoice[]>([]);
  const [lastYear, setLastYear] = React.useState("");
  const [marketErrors, setMarketErrors] = React.useState<boolean[]>([false]);

  React.useEffect(() => {
    if (searchForm.stateNames.length !== marketErrors.length) {
      setMarketErrors(new Array(searchForm.stateNames.length).fill(false));
    }
  }, [marketErrors.length, searchForm.stateNames.length]);

  React.useEffect(() => {
    if (
      searchForm.locations === "msa" &&
      (searchForm.stalpbr || Object.keys(searchForm.marketSelection[0]).length)
    ) {
      setSearchForm({
        ...searchForm,
        stalpbr: "",
        marketSelection: [{}],
        stateNames: [""],
      });
    }
    if (
      searchForm.locations === "countyByState" &&
      (searchForm.msa || Object.keys(searchForm.marketSelection[0]).length)
    ) {
      setSearchForm({
        ...searchForm,
        msa: "",
        marketSelection: [{}],
        stateNames: [""],
      });
    }
    if (
      searchForm.locations === "marketSelect" &&
      (searchForm.msa || searchForm.stalpbr)
    ) {
      setSearchForm({
        ...searchForm,
        msa: "",
        stalpbr: "",
      });
    }
  }, [searchForm, setSearchForm]);

  if (
    searchForm.locations === "zip" &&
    (searchForm.msa || Object.keys(searchForm.marketSelection[0]).length)
  ) {
    setSearchForm({
      ...searchForm,
      msa: "",
      marketSelection: [{}],
    });
  }
  //sets errors
  React.useEffect(() => {
    setDisabled(!searchForm.locations);
    if (searchForm.locations === "marketSelect") {
      setDisabled(
        marketErrors.includes(true) || searchForm.stateNames[0] === "",
      );
    }
    if (searchForm.locations === "countyByState") {
      setDisabled(!searchForm.stalpbr);
    }
    if (searchForm.locations === "msa") {
      setDisabled(!searchForm.msa);
    }
  }, [
    disabled,
    marketErrors,
    searchForm.locations,
    searchForm.msa,
    searchForm.stalpbr,
    searchForm.stateNames,
    searchForm,
    setDisabled,
  ]);
  //fetch list of MSAs
  React.useEffect(() => {
    let isMounted = true;
    if (lastYear !== searchForm.lastYear && searchForm.locations === "msa") {
      setLastYear(searchForm.lastYear);
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod?filters=YEAR:${
            searchForm.lastYear
          }&agg_by=MSANAMB&agg_limit=10000`,
        )
          .then(response => {
            if (isMounted) {
              setMsa(
                response.data
                  .map((e: KeyValue) => {
                    return {
                      label: e.MSANAMB,
                      value: e.MSANAMB,
                    };
                  })
                  .filter((value: KeyValue) => value.value),
              );
            }
          })
          .catch(error => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            isMounted = false;
          });
      })();
    }
  }, [lastYear, searchForm.lastYear, searchForm.locations]);
  return (
    <List id="depostMarketShare">
      <ListItem>
        <Typography className={classes.title} variant="h4">
          Locations
        </Typography>
        <SelectFilterField
          currentValue={searchForm.locations}
          fieldName="locations"
          options={
            searchForm.reportType === "proForma"
              ? proFormaLocationOptions
              : searchForm.institutionType === "bhcs" &&
                searchForm.reportType !== "proForma"
              ? marketShareLocationOptions2
              : marketShareLocationOptions
          }
          form={searchForm}
          setForm={setSearchForm}
        />
      </ListItem>
      {searchForm.locations === "marketSelect" && (
        <>
          {searchForm.stateNames.map((state: string, int: number) => {
            return (
              <React.Fragment key={int}>
                <BranchOfficeMarketSelections
                  filtersAndSearch={{
                    filters: {
                      lastYear: searchForm.lastYear,
                      stateName: searchForm.stateNames,
                    },
                    search: {},
                  }}
                  currentIndex={int}
                  searchForm={searchForm}
                  setSearchForm={setSearchForm}
                  errors={marketErrors}
                  setErrors={setMarketErrors}
                />
                <Grid container spacing={2} className={classes.buttonsGrid}>
                  <Grid item sm={6}>
                    {searchForm.stateNames.length > 1 && (
                      <Button
                        color="secondary"
                        variant="contained"
                        id={`remove_${state}`}
                        className={classes.addRemoveButtons}
                        onClick={() =>
                          setSearchForm({
                            ...searchForm,
                            stateNames: searchForm.stateNames.filter(
                              (state: string, index: number) => index !== int,
                            ),
                            marketSelection: searchForm.marketSelection.filter(
                              (selection: KeyValue, index: number) =>
                                index !== int,
                            ),
                          })
                        }
                      >
                        Remove
                      </Button>
                    )}
                  </Grid>
                  {(appState.filters.cert !== "" ||
                    appState.filters.bhcRSSD !== "" ||
                    (searchForm.marketSelection[0] &&
                      Object.keys(searchForm.marketSelection[0]).length > 0) ||
                    (appState.ui.marketSelection[0] &&
                      Object.keys(appState.ui.marketSelection[0]).length >
                        0)) &&
                    searchForm.stateNames[int] !== "" &&
                    searchForm.stateNames[0] !== "" &&
                    searchForm.stateNames[0] !== "ALL" && (
                      <Grid item sm={6}>
                        <Button
                          color="secondary"
                          variant="contained"
                          className={clsx(
                            classes.addRemoveButtons,
                            classes.addButton,
                          )}
                          onClick={() =>
                            setSearchForm({
                              ...searchForm,
                              stateNames: [...searchForm.stateNames, ""],
                            })
                          }
                        >
                          Add Another
                        </Button>
                      </Grid>
                    )}
                </Grid>
              </React.Fragment>
            );
          })}
        </>
      )}

      {searchForm.locations === "countyByState" && (
        <>
          <ListItem>
            <SelectFilterField
              currentValue={searchForm.stalpbr}
              fieldName="stalpbr"
              options={[
                {
                  label: "Select State/Territory",
                  value: "",
                },
                ...selectedStatesAlpha,
              ]}
              form={searchForm}
              setForm={setSearchForm}
            />
          </ListItem>
        </>
      )}
      {searchForm.locations === "msa" && (
        <ListItem>
          <SelectFilterField
            currentValue={searchForm.msa}
            fieldName="msa"
            options={[{ label: "Select MSA", value: "" }, ...msa]}
            form={searchForm}
            setForm={setSearchForm}
          />
        </ListItem>
      )}
      {searchForm.locations === "zip" && (
        <ListItem>
          <TextFilterField
            currentValue={searchForm.zipbr}
            apiPath="institutions"
            fieldName="zipbr"
            placeholder="Enter ZIP Code"
            regEx={numericalRegEx}
            form={searchForm}
            setForm={setSearchForm}
          />
        </ListItem>
      )}
      {divider}
    </List>
  );
}
